import { styled, keyframes, css } from "styled-components";
import { FaMoon, FaSignOutAlt } from "react-icons/fa";

import { ReactComponent as FaCirclePlay } from "../../assets/icons/play.svg";
import { ReactComponent as FaCirclePause } from "../../assets/icons/pause.svg";
import { Skeleton } from "../skeleton/Skeleton.styles";

/* ─── Motion ─────────────────────────────────────────────────────────────── */

const pearlPulse = keyframes`
  0%, 100% {
    filter:
      drop-shadow(0 4px 10px rgba(var(--color-brand-600-rgb), 0.38))
      drop-shadow(0 1px 3px rgba(var(--color-brand-400-rgb), 0.28));
  }
  50% {
    filter:
      drop-shadow(0 6px 14px rgba(var(--color-brand-600-rgb), 0.52))
      drop-shadow(0 2px 5px rgba(var(--color-brand-400-rgb), 0.42));
  }
`;

const glossShimmer = keyframes`
  0%, 100% {
    opacity: 0.75;
    transform: scale(0.9);
  }
  50% {
    opacity: 1;
    transform: scale(1.08);
  }
`;

/* ─── Outer container ────────────────────────────────────────────────────── */

export const HeaderContainer = styled.header`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1100;
  padding: 14px 18px;
  pointer-events: none;
  overflow: visible;
  will-change: transform, opacity;

  /* Auto-hide: slide off-screen when CollectionFilter scrolls under the header
     on scroll-down; slide back in on scroll-up (see Header.js logic). */
  transform: ${({ $offScreen }) =>
    $offScreen ? "translateY(-120%)" : "translateY(0)"};
  opacity: ${({ $offScreen }) => ($offScreen ? 0 : 1)};

  transition:
    transform 560ms var(--ease-snappy, cubic-bezier(0.4, 0, 0.2, 1)),
    opacity 440ms var(--ease-snappy, cubic-bezier(0.4, 0, 0.2, 1));

  @media (max-width: 768px) {
    padding: 10px 14px;
    html.feed-scroll-mode & {
      transform: translateY(-110%);
      opacity: 0;
    }
  }
`;

/* ─── The liquid-glass bar ───────────────────────────────────────────────── */

export const HeaderWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 1080px;
  height: 68px;
  /* Flex with space-between for left/right segments; the logo sits in
     the middle as an absolutely-positioned element so it's anchored
     to the geometric center of the bar regardless of how wide the
     left (menu) or right (credits) sections grow. */
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 18px;
  pointer-events: auto;
  overflow: visible;

  /* Water-shaped rounded ends — liquid pill */
  border-radius: 34px;
  border: 1px solid
    ${(p) =>
      p.$hidden ? "rgba(var(--color-brand-300-rgb), 0)" : "var(--glass-edge)"};

  /* Layered frosted glass: radial top-light + subtle aqua refraction.
     Light glass uses white→pale-blue washes; in dark theme the override
     below swaps the base layers to a deep navy panel — same brand tint,
     inverted lightness. */
  background: ${(p) =>
    p.$hidden
      ? "transparent"
      : `
        radial-gradient(
          120% 80% at 50% -10%,
          var(--glass-base-1) 0%,
          var(--glass-tint-1) 45%,
          var(--glass-tint-2) 100%
        ),
        linear-gradient(
          180deg,
          var(--glass-base-2) 0%,
          var(--glass-tint-3) 100%
        )
      `};

  box-shadow: ${(p) =>
    p.$hidden
      ? "none"
      : `
        0 22px 48px -18px rgba(var(--color-brand-500-rgb), 0.35),
        0 8px 24px rgba(var(--color-brand-400-rgb), 0.16),
        inset 0 2px 0 rgba(255, 255, 255, 0.95),
        inset 0 -12px 22px rgba(var(--color-brand-200-rgb), 0.28),
        inset 1px 0 0 rgba(255, 255, 255, 0.6),
        inset -1px 0 0 rgba(var(--color-brand-300-rgb), 0.18)
      `};

  [data-theme="dark"] & {
    box-shadow: ${(p) =>
      p.$hidden
        ? "none"
        : `
          0 18px 32px -16px rgba(0, 0, 0, 0.55),
          0 6px 14px rgba(0, 0, 0, 0.35)
        `};
  }

  /* Lower blur radius (was 28px). The header is fixed, so the browser
     re-rasters everything behind it on every scroll frame; cost scales
     roughly with blur radius². 10-12px is visually almost identical for
     the frosted-glass look but ~6× cheaper to composite. */
  backdrop-filter: ${(p) => (p.$hidden ? "none" : "blur(12px) saturate(180%)")};
  -webkit-backdrop-filter: ${(p) =>
    p.$hidden ? "none" : "blur(12px) saturate(180%)"};

  /* Top specular highlight — the "lit" edge of the water pill */
  &::before {
    content: "";
    position: absolute;
    top: 3px;
    left: 28px;
    right: 28px;
    height: 22px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      var(--glass-sheen) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2px);
    opacity: ${(p) => (p.$hidden ? 0 : 1)};
    pointer-events: none;
    transition: opacity 320ms var(--ease-snappy);
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  /* Bottom inner refraction — darker aqua tint */
  &::after {
    content: "";
    position: absolute;
    bottom: 2px;
    left: 40%;
    right: 40%;
    height: 10px;
    border-radius: 999px;
    background: radial-gradient(
      ellipse at center,
      rgba(var(--color-brand-400-rgb), 0.3) 0%,
      rgba(var(--color-brand-400-rgb), 0) 70%
    );
    opacity: ${(p) => (p.$hidden ? 0 : 0.6)};
    pointer-events: none;
    transition: opacity 320ms var(--ease-snappy);
  }

  /* No backdrop-filter in the transition list — animating it forces a
     fresh full-rect raster on every step, which spikes the GPU exactly
     when the user scrolls (which is when $hidden flips). */
  transition:
    background 320ms var(--ease-snappy),
    border-color 320ms var(--ease-snappy),
    box-shadow 320ms var(--ease-snappy);

  @media (max-width: 768px) {
    height: 60px;
    padding: 0 8px;
    border-radius: 28px;
  }
  @media (max-width: 370px) {
    height: unset;
    padding: 8px;
  }
`;

/* ─── Left: round water-pearl menu button ────────────────────────────────── */

/* Animated hamburger → X icon (3 soft gradient bars that morph on $open) */
export const HamburgerIcon = styled.span`
  position: relative;
  display: inline-block;
  width: 18px;
  height: 14px;
  pointer-events: none;
  z-index: 1;

  & > span {
    position: absolute;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(
      90deg,
      var(--color-brand-400) 0%,
      var(--color-brand-600) 100%
    );
    border-radius: 999px;
    transform-origin: 50% 50%;
    box-shadow:
      0 1px 2px rgba(var(--color-brand-600-rgb), 0.22),
      inset 0 0.5px 0 rgba(255, 255, 255, 0.45);
    transition:
      transform 320ms var(--ease-snappy),
      top 240ms var(--ease-snappy) 80ms,
      width 240ms var(--ease-snappy),
      opacity 160ms var(--ease-snappy);
  }

  & > span:nth-child(1) {
    top: ${(p) => (p.$open ? "6px" : "0")};
    transform: rotate(${(p) => (p.$open ? "45deg" : "0")});
    transition:
      transform 320ms var(--ease-snappy) ${(p) => (p.$open ? "140ms" : "0ms")},
      top 180ms var(--ease-snappy) ${(p) => (p.$open ? "0ms" : "140ms")};
  }

  & > span:nth-child(2) {
    top: 6px;
    width: ${(p) => (p.$open ? "30%" : "70%")};
    left: ${(p) => (p.$open ? "35%" : "30%")};
    opacity: ${(p) => (p.$open ? 0 : 1)};
    transform: scaleX(${(p) => (p.$open ? 0.2 : 1)});
    transition:
      opacity 180ms var(--ease-snappy),
      left 220ms var(--ease-snappy),
      width 220ms var(--ease-snappy),
      transform 180ms var(--ease-snappy);
  }

  & > span:nth-child(3) {
    top: ${(p) => (p.$open ? "6px" : "12px")};
    transform: rotate(${(p) => (p.$open ? "-45deg" : "0")});
    transition:
      transform 320ms var(--ease-snappy) ${(p) => (p.$open ? "140ms" : "0ms")},
      top 180ms var(--ease-snappy) ${(p) => (p.$open ? "0ms" : "140ms")};
  }
`;

export const MenuButton = styled.button`
  position: relative;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid var(--glass-edge-soft);
  background: radial-gradient(
    130% 100% at 30% 15%,
    var(--glass-base-2) 0%,
    var(--glass-base-3) 45%,
    var(--glass-tint-3) 100%
  );
  backdrop-filter: blur(10px) saturate(185%);
  -webkit-backdrop-filter: blur(10px) saturate(185%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--color-brand-700);
  outline: none;
  overflow: hidden;
  box-shadow:
    0 8px 20px rgba(var(--color-brand-500-rgb), 0.14),
    0 2px 6px rgba(var(--color-brand-500-rgb), 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -4px 12px rgba(var(--color-brand-200-rgb), 0.22),
    inset 0 0 0 1px rgba(255, 255, 255, 0.28);

  [data-theme="dark"] & {
    box-shadow:
      0 6px 14px rgba(0, 0, 0, 0.5),
      0 2px 6px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.12),
      inset 0 -4px 12px rgba(var(--color-brand-700-rgb), 0.16),
      inset 0 0 0 1px rgba(255, 255, 255, 0.06);
  }
  transition:
    transform 220ms var(--ease-snappy),
    box-shadow 220ms var(--ease-snappy),
    background 220ms var(--ease-snappy);

  /* Top-left soft sheen — real white so it survives dark theme. */
  &::before {
    content: "";
    position: absolute;
    top: 3px;
    left: 6px;
    width: 22px;
    height: 12px;
    border-radius: 50%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.85) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
    opacity: 0.9;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  [data-theme="dark"] &::before {
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.18) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    opacity: 0.85;
  }

  /* Bottom aqua underglow — gives the glass depth */
  &::after {
    content: "";
    position: absolute;
    bottom: -2px;
    left: 18%;
    right: 18%;
    height: 10px;
    border-radius: 50%;
    background: radial-gradient(
      ellipse at center top,
      rgba(var(--color-brand-400-rgb), 0.3) 0%,
      rgba(var(--color-brand-400-rgb), 0) 70%
    );
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-1px) scale(1.03);
    background: radial-gradient(
      130% 100% at 30% 15%,
      var(--glass-base-1) 0%,
      var(--glass-base-2) 45%,
      var(--glass-tint-3) 100%
    );
    box-shadow:
      0 12px 26px rgba(var(--color-brand-500-rgb), 0.22),
      0 3px 8px rgba(var(--color-brand-500-rgb), 0.12),
      inset 0 1px 0 rgba(255, 255, 255, 1),
      inset 0 -4px 14px rgba(var(--color-brand-200-rgb), 0.32),
      inset 0 0 0 1px rgba(255, 255, 255, 0.38);
  }

  [data-theme="dark"] &:hover {
    box-shadow:
      0 8px 18px rgba(0, 0, 0, 0.55),
      0 3px 8px rgba(0, 0, 0, 0.32),
      inset 0 1px 0 rgba(255, 255, 255, 0.18),
      inset 0 -4px 14px rgba(var(--color-brand-700-rgb), 0.2),
      inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  }

  &:active {
    transform: translateY(0) scale(0.97);
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }

  @media (max-width: 370px) {
    width: 38px;
    height: 38px;
  }
`;

/* ─── Center: wordmark logo ──────────────────────────────────────────────── */

/* Absolutely centered against the HeaderWrapper. Grid columns are
   `auto 1fr auto`, so when the left (menu) and right (credits/avatar)
   sides have different widths the 1fr center column drifts off-axis,
   which pulled the logo away from the Header's true center. Anchoring
   on left:50% + translate keeps it locked to the geometric middle no
   matter how wide either side gets. */
export const LogoWrapper = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;

  /* Soft aqua halo around the logo */
  &::after {
    content: "";
    position: absolute;
    inset: -10px -24px;
    background: radial-gradient(
      ellipse at center,
      rgba(var(--color-brand-400-rgb), 0.22) 0%,
      rgba(var(--color-brand-400-rgb), 0) 70%
    );
    filter: blur(6px);
    pointer-events: none;
    z-index: -1;
  }

  @media (max-width: 370px) {
    left: 55px;
    transform: translateY(-40%);
  }
`;

export const LogoImg = styled.img`
  height: 42px;
  width: auto;
  display: block;
  filter: drop-shadow(0 6px 14px rgba(var(--color-brand-400-rgb), 0.3))
    drop-shadow(0 2px 4px rgba(var(--glass-white-rgb), 0.6));

  @media (max-width: 768px) {
    height: 34px;
  }
  @media (max-width: 370px) {
    height: 30px;
  }
`;

export const LogoTagline = styled.span`
  margin-top: -2px;
  white-space: nowrap;
  font-family: var(--font-family-primary);
  font-size: 7.5px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  background: linear-gradient(
    180deg,
    var(--color-brand-300) 0%,
    var(--color-brand-500) 44%,
    var(--color-brand-700) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: var(--color-brand-600);
  filter: drop-shadow(0 1px 0 rgba(var(--glass-white-rgb), 0.9))
    drop-shadow(0 2px 3px rgba(var(--color-brand-600-rgb), 0.22));

  @media (max-width: 768px) {
    margin-top: -1px;
    font-size: 6.5px;
    letter-spacing: 0.08em;
  }
`;

/* ─── Right side ─────────────────────────────────────────────────────────── */

export const HeaderRightSide = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  justify-self: end;
`;

/* Legacy button kept for any consumers — redesigned as liquid pill */
export const UpgradeButton = styled.button`
  font-size: 14px;
  font-family: var(--font-family-primary);
  font-weight: 700;
  padding: 10px 18px;
  background: linear-gradient(
    180deg,
    var(--color-brand-300) 0%,
    var(--color-brand-500) 100%
  );
  color: #ffffff;
  outline: none;
  border: 1px solid rgba(var(--glass-white-rgb), 0.6);
  border-radius: var(--radius-pill);
  box-shadow:
    0 10px 22px rgba(var(--color-brand-500-rgb), 0.28),
    inset 0 2px 0 rgba(255, 255, 255, 0.85);
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    transform: translateY(-1px);
    box-shadow:
      0 14px 28px rgba(var(--color-brand-500-rgb), 0.4),
      inset 0 2px 0 rgba(255, 255, 255, 0.95);
  }

  @media (max-width: 768px) {
    display: none;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

/* ─── Credits pill (frosted white with blue-droplet coin) ────────────────── */

export const ProUpgradeButtonContainer = styled.div`
  position: relative;
  border-radius: var(--radius-pill);
  /* "Water outline" — white inner rim against aqua glow halo */
  border: 1.5px solid var(--glass-edge);
  background:
    radial-gradient(
      140% 100% at 20% 0%,
      rgba(255, 255, 255, 0.32) 0%,
      rgba(255, 255, 255, 0) 55%
    ),
    linear-gradient(
      180deg,
      var(--color-brand-400) 0%,
      var(--color-brand-600) 55%,
      var(--color-brand-700) 100%
    );
  color: #ffffff;
  gap: 3px;

  box-shadow:
    /* outer water halo ring — the "outline" effect reads as wetness */
    0 0 0 3px rgba(var(--color-brand-300-rgb), 0.22),
    0 0 0 1px rgba(var(--glass-white-rgb), 0.35),
    /* deep brand drop shadow */ 0 14px 28px
      rgba(var(--color-brand-600-rgb), 0.38),
    0 4px 10px rgba(var(--color-brand-600-rgb), 0.2),
    /* inner specular rim + underglow */ inset 0 1px 0 rgba(255, 255, 255, 0.9),
    inset 0 -3px 8px rgba(var(--color-brand-700-rgb), 0.38),
    inset 0 0 0 1px rgba(255, 255, 255, 0.35);

  [data-theme="dark"] & {
    box-shadow:
      0 0 0 1px rgba(255, 255, 255, 0.06),
      0 12px 22px rgba(0, 0, 0, 0.55),
      0 4px 8px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.55),
      inset 0 -3px 8px rgba(0, 0, 0, 0.25);
    border: none;
    &::after {
      opacity: 0.35;
    }
  }
  height: 42px;
  padding: 4px 16px 4px 8px;
  display: inline-flex;
  align-items: center;

  cursor: pointer;
  isolation: isolate;

  transition:
    transform 220ms var(--ease-snappy),
    box-shadow 220ms var(--ease-snappy),
    filter 220ms var(--ease-snappy);

  /* Top sheen — bright specular light on the pill's upper edge.
     The pill background stays brand-blue in dark mode, so this top
     highlight must remain a real white (not the inverted token). */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 14px;
    right: 14px;
    height: 10px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.85) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1.5px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  /* Bottom aqua underglow — extra water-outline feel at the bottom */
  &::after {
    content: "";
    position: absolute;
    bottom: -6px;
    left: 20%;
    right: 20%;
    height: 14px;
    background: radial-gradient(
      ellipse at center top,
      rgba(var(--color-brand-400-rgb), 0.5) 0%,
      rgba(var(--color-brand-400-rgb), 0) 70%
    );
    pointer-events: none;
    z-index: -1;
  }

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.06);
    box-shadow:
      0 0 0 4px rgba(var(--color-brand-300-rgb), 0.32),
      0 0 0 1px rgba(var(--glass-white-rgb), 0.5),
      0 20px 38px rgba(var(--color-brand-600-rgb), 0.5),
      0 6px 14px rgba(var(--color-brand-600-rgb), 0.28),
      inset 0 1px 0 rgba(255, 255, 255, 1),
      inset 0 -3px 10px rgba(var(--color-brand-700-rgb), 0.44),
      inset 0 0 0 1px rgba(255, 255, 255, 0.5);
  }

  [data-theme="dark"] &:hover {
    border: none;

    box-shadow:
      0 0 0 1px rgba(255, 255, 255, 0.1),
      0 16px 30px rgba(0, 0, 0, 0.6),
      0 6px 12px rgba(0, 0, 0, 0.36),
      inset 0 1px 0 rgba(255, 255, 255, 0.22),
      inset 0 -3px 10px rgba(0, 0, 0, 0.3);
  }

  svg {
    filter: drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.25));
  }

  @media (max-width: 768px) {
    height: 38px;
    padding: 3px 14px 3px 6px;
    gap: 8px;
  }
`;

/* Realistic 3D water-drop icon — layered highlights inside an SVG mask */
export const CreditDrop = styled.span`
  position: relative;
  display: inline-block;
  flex-shrink: 0;
  width: ${(p) =>
    p.$size === "sm" ? "14px" : p.$size === "lg" ? "40px" : "22px"};
  height: ${(p) =>
    p.$size === "sm" ? "16px" : p.$size === "lg" ? "46px" : "25px"};
  margin: ${(p) =>
    p.$size === "sm"
      ? "0 2px 0 0"
      : p.$size === "lg"
        ? "0 6px 0 4px"
        : "0 3px 0 2px"};
  animation: ${pearlPulse} 5s var(--ease-snappy) infinite;

  /* The 3D drop:
     Layer order (top → bottom):
     1. Sharp white reflection dot (glass glint)
     2. Bright specular highlight (upper-left bulb)
     3. Soft inner upper-glow
     4. Base sphere gradient — light top-left → brand blue → deep navy bottom
     All layers are clipped by the drop-shape SVG mask. */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    /* Two color schemes:
       - default ("white"): white-cyan body, glows on dark surfaces
       - "blue": deep-aqua body, reads on light surfaces  */
    background: ${(p) =>
      p.$variant === "blue"
        ? `
      /* (1) Bright white reflection dot */
      radial-gradient(
          circle at 30% 38%,
          rgba(var(--glass-white-rgb), 1) 0%,
          rgba(var(--glass-pale-rgb), 0.92) 25%,
          rgba(var(--color-brand-200-rgb), 0) 60%
        )
        0 0 / 18% 14% no-repeat,
      /* (2) Curved gloss stroke — light cyan on deep blue */
      url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath fill='rgb(180, 230, 255)' opacity='0.85' d='M272 412a12 12 0 0 1-11.34-16a11.89 11.89 0 0 1 11.41-8A60.06 60.06 0 0 0 332 328.07a11.89 11.89 0 0 1 8-11.41A12 12 0 0 1 356 328a84.09 84.09 0 0 1-84 84'/%3E%3C/svg%3E")
        no-repeat center / contain,
      /* (3) Soft elongated specular — light cyan */
      radial-gradient(
          ellipse 44% 32% at 34% 46%,
          rgba(var(--color-brand-200-rgb), 0.65) 0%,
          rgba(var(--color-brand-300-rgb), 0.3) 50%,
          rgba(var(--color-brand-300-rgb), 0) 100%
        ),
      /* (4) Base body — light at top-left, deep brand blue at bottom */
      radial-gradient(
          ellipse 115% 100% at 34% 28%,
          #ffffff 0%,
          var(--color-brand-200) 12%,
          var(--color-brand-300) 35%,
          var(--color-brand-500) 65%,
          var(--color-brand-700) 100%
        )`
        : `
      /* (1) Bright light-blue reflection dot */
      radial-gradient(
          circle at 30% 38%,
          rgba(130, 205, 255, 1) 0%,
          rgba(var(--color-brand-300-rgb), 0.92) 25%,
          rgba(var(--color-brand-200-rgb), 0) 60%
        )
        0 0 / 18% 14% no-repeat,
      /* (2) Curved gloss stroke — medium-sky blue */
      url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath fill='rgb(108, 196, 255)' opacity='0.92' d='M272 412a12 12 0 0 1-11.34-16a11.89 11.89 0 0 1 11.41-8A60.06 60.06 0 0 0 332 328.07a11.89 11.89 0 0 1 8-11.41A12 12 0 0 1 356 328a84.09 84.09 0 0 1-84 84'/%3E%3C/svg%3E")
        no-repeat center / contain,
      /* (3) Soft elongated specular — light sky blue */
      radial-gradient(
          ellipse 44% 32% at 34% 46%,
          rgba(var(--color-brand-300-rgb), 0.65) 0%,
          rgba(var(--color-brand-200-rgb), 0.3) 50%,
          rgba(var(--color-brand-200-rgb), 0) 100%
        ),
      /* (4) Base body — near-white with a soft cyan center */
      radial-gradient(
          ellipse 115% 100% at 34% 28%,
          #ffffff 0%,
          #f4fbff 30%,
          #e4f4ff 60%,
          var(--color-brand-200) 92%,
          var(--color-brand-300) 100%
        )`};
    -webkit-mask: url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath d='M265.12 60.12a12 12 0 0 0-18.23 0C215.23 97.15 112 225.17 112 320c0 88.37 55.64 144 144 144s144-55.63 144-144c0-94.83-103.23-222.85-134.88-259.88'/%3E%3C/svg%3E")
      no-repeat center / contain;
    mask: url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath d='M265.12 60.12a12 12 0 0 0-18.23 0C215.23 97.15 112 225.17 112 320c0 88.37 55.64 144 144 144s144-55.63 144-144c0-94.83-103.23-222.85-134.88-259.88'/%3E%3C/svg%3E")
      no-repeat center / contain;
    filter: ${(p) =>
      p.$variant === "blue"
        ? `drop-shadow(0 2px 4px rgba(var(--color-brand-900-rgb), 0.32))
          drop-shadow(0 0 6px rgba(var(--color-brand-400-rgb), 0.28))`
        : `drop-shadow(0 1px 2px rgba(var(--color-brand-900-rgb), 0.45))
          drop-shadow(0 0 4px rgba(var(--glass-white-rgb), 0.35))`};
  }

  /* Animated micro-glint — a small shimmering bright dot on top of the specular
     area, giving the drop a "live" glassy feel. Sits above ::before in DOM. */
  &::after {
    content: "";
    position: absolute;
    top: 34%;
    left: 28%;
    width: 12%;
    height: 10%;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      rgba(var(--glass-white-rgb), 1) 0%,
      rgba(255, 255, 255, 0.6) 35%,
      rgba(255, 255, 255, 0) 75%
    );
    filter: blur(0.3px);
    pointer-events: none;
    animation: ${glossShimmer} 4.8s ease-in-out infinite;
  }

  [data-theme="dark"] &::after {
    opacity: 0;
  }
`;
/* Reusable chip base — used by Category (tall) and Tag (smaller) variants */
const chipBase = css`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  gap: 8px;
  border-radius: var(--radius-pill);
  cursor: pointer;
  white-space: nowrap;
  user-select: none;
  transition:
    transform 220ms var(--ease-snappy),
    border-color 220ms var(--ease-snappy),
    background 220ms var(--ease-snappy),
    box-shadow 220ms var(--ease-snappy),
    color 220ms var(--ease-snappy);

  &::before {
    content: "";
    position: absolute;
    top: 1px;
    left: 12px;
    right: 12px;
    height: 7px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.55) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }
`;

const resolveChipWidth = (width) => {
  if (width === undefined || width === null) return "auto";
  return typeof width === "number" ? `${width}px` : width;
};

const shimmer = keyframes`
  0%   { background-position: -468px 0; }
  100% { background-position:  468px 0; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.7; }
`;

export const ProUpgradeButtonSkeleton = styled(Skeleton)`
  ${chipBase}

  height: ${(p) => (p.$height ? p.$height : "42px")};
  min-width: ${({ $width }) => resolveChipWidth($width)};
  padding: 0 20px;
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: ${({ $selected }) => ($selected ? 700 : 600)};
  letter-spacing: -0.01em;
  color: ${({ $selected }) =>
    $selected ? "#ffffff" : "var(--color-brand-700)"};
  text-shadow: ${({ $selected }) =>
    $selected ? "0 1px 0 rgba(var(--color-brand-900-rgb), 0.28)" : "none"};
  border: 1px solid
    ${({ $selected }) =>
      $selected
        ? "rgba(var(--glass-white-rgb), 0.8)"
        : "rgba(var(--glass-white-rgb), 0.65)"};
  background: ${({ $selected }) =>
    $selected
      ? `radial-gradient(
             140% 100% at 20% 0%,
             rgba(255, 255, 255, 0.3) 0%,
             rgba(255, 255, 255, 0) 55%
           ),
           linear-gradient(180deg, var(--color-brand-400) 0%, var(--color-brand-600) 55%, var(--color-brand-700) 100%)`
      : `radial-gradient(
             120% 100% at 20% 0%,
             rgba(var(--glass-white-rgb), 0.88) 0%,
             rgba(var(--glass-pale-rgb), 0.66) 55%,
             rgba(var(--glass-pale-rgb), 0.5) 100%
           )`};
  box-shadow: ${({ $selected }) =>
    $selected
      ? `0 2px 5px rgba(var(--color-brand-600-rgb), 0.28),
           0 1px 3px rgba(var(--color-brand-600-rgb), 0.14),
           inset 0 1px 0 rgba(255, 255, 255, 0.6),
           inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.3)`
      : `0 1.5px 3px rgba(var(--color-brand-500-rgb), 0.12),
           inset 0 1px 0 rgba(255, 255, 255, 0.92),
           inset 0 -2px 6px rgba(var(--color-brand-200-rgb), 0.2)`};
  /* selectedGlow keyframe dropped — animating box-shadow forces a full
       repaint per frame even though the chip never moves. */

  ${({ $animation }) =>
    $animation &&
    css`
      background: linear-gradient(
        90deg,
        rgba(var(--glass-pale-rgb), 0.8) 0px,
        rgba(var(--glass-pale-rgb), 0.9) 40px,
        rgba(var(--glass-pale-rgb), 0.8) 80px
      );
      background-size: 468px 100%;
      animation:
        ${shimmer} 3s infinite linear,
        ${pulse} 2s infinite ease-in-out;
      &::before {
        display: none;
      }
    `}

  svg {
    width: 18px;
    height: 18px;
    path {
      stroke: ${({ $selected }) =>
        $selected ? "#ffffff" : "var(--color-brand-700)"};
    }
    circle {
      stroke: ${({ $selected }) =>
        $selected ? "#ffffff" : "var(--color-brand-700)"};
    }
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-1px);
    }
  }

  @media (max-width: 480px) {
    height: 42px;
    padding: 0 16px;
    font-size: 13px;
  }
`;

export const CreditText = styled.span`
  font-family: var(--font-family-primary);
  font-size: 16px;
  font-weight: 600;
  line-height: normal;
  letter-spacing: -0.02em;

  /* Solid white against the brand-blue pill background */
  color: #ffffff;
  text-shadow:
    0 1px 1px rgba(var(--color-brand-900-rgb), 0.38),
    0 0 6px rgba(var(--color-brand-300-rgb), 0.35);
  font-variant-numeric: tabular-nums;

  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: geometricPrecision;

  padding-right: 2px;
  @media (max-width: 370px) {
    font-size: 14px;
  }
`;

export const ProfileSection = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  > svg {
    width: 42px;
    height: 42px;
    padding: 10px;
    border-radius: 50%;
    color: var(--color-brand-700);
    border: 1px solid rgba(var(--glass-white-rgb), 0.9);
    background: radial-gradient(
      circle at 30% 22%,
      rgba(var(--glass-white-rgb), 0.98) 0%,
      rgba(var(--glass-pale-rgb), 0.82) 50%,
      rgba(198, 226, 255, 0.62) 100%
    );
    box-shadow:
      0 10px 22px rgba(var(--color-brand-500-rgb), 0.2),
      inset 0 2px 0 rgba(255, 255, 255, 0.95),
      inset 0 -6px 12px rgba(var(--color-brand-200-rgb), 0.36);
    cursor: pointer;
    transition:
      transform 180ms var(--ease-snappy),
      box-shadow 180ms var(--ease-snappy);
  }

  > svg:hover {
    transform: translateY(-1px) scale(1.03);
    box-shadow:
      0 14px 28px rgba(var(--color-brand-500-rgb), 0.3),
      inset 0 2px 0 rgba(255, 255, 255, 1),
      inset 0 -6px 14px rgba(var(--color-brand-200-rgb), 0.45);
  }

  @media (max-width: 768px) {
    > svg {
      width: 40px;
      height: 40px;
    }
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

export const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 14px);
  right: 0;
  z-index: 1100;
  animation: ${(props) => (props.$animating ? "ddFadeOut" : "ddFadeIn")} 0.22s
    var(--ease-snappy) forwards;

  display: flex;
  width: 290px;
  padding: 0;
  flex-direction: column;
  border-radius: 26px;
  border: 1px solid var(--glass-edge);
  background:
    radial-gradient(
      120% 60% at 30% -10%,
      var(--glass-base-1) 0%,
      var(--glass-tint-soft) 60%,
      var(--glass-tint-3) 100%
    ),
    linear-gradient(180deg, var(--glass-base-1), var(--glass-tint-soft));
  box-shadow:
    0 28px 52px -14px rgba(var(--color-brand-500-rgb), 0.36),
    0 10px 26px rgba(var(--color-brand-400-rgb), 0.18),
    inset 0 2px 0 rgba(255, 255, 255, 0.95),
    inset 0 -10px 18px rgba(var(--color-brand-200-rgb), 0.26);

  [data-theme="dark"] & {
    box-shadow:
      0 24px 48px -14px rgba(0, 0, 0, 0.6),
      0 8px 20px rgba(0, 0, 0, 0.36),
      inset 0 1px 0 rgba(255, 255, 255, 0.06),
      inset 0 -10px 18px rgba(var(--color-brand-700-rgb), 0.16);
  }
  backdrop-filter: blur(12px) saturate(180%);
  -webkit-backdrop-filter: blur(12px) saturate(180%);
  overflow: hidden;

  /* Top specular reflection on the panel — real white so the highlight
     survives dark theme. */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 24px;
    right: 24px;
    height: 18px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.85) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  [data-theme="dark"] &::before {
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.18) 0%,
      rgba(255, 255, 255, 0) 100%
    );
  }

  @keyframes ddFadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px) scale(0.96);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes ddFadeOut {
    from {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
    to {
      opacity: 0;
      transform: translateY(-10px) scale(0.96);
    }
  }
`;

export const DropdownUserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid rgba(var(--color-brand-300-rgb), 0.18);
  position: relative;
`;

export const DropdownAvatar = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1px solid rgba(var(--glass-white-rgb), 0.9);
  background: radial-gradient(
    circle at 30% 25%,
    rgba(var(--glass-white-rgb), 0.98) 0%,
    rgba(186, 220, 255, 0.8) 50%,
    rgba(118, 182, 244, 0.7) 100%
  );
  color: var(--color-brand-700);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 18px;
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.24),
    inset 0 2px 0 rgba(255, 255, 255, 0.95);

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

export const DropdownUserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

export const DropdownUsername = styled.span`
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const DropdownCredits = styled.span`
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  [data-theme="dark"] & {
    color: var(--color-brand-300);
  }
`;

export const DropdownNav = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px;
`;

export const DropdownItem = styled.div`
  cursor: pointer;
  transition:
    background 160ms var(--ease-snappy),
    color 160ms var(--ease-snappy),
    transform 160ms var(--ease-snappy);
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.01em;
  padding: 10px 12px;
  border-radius: 14px;

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    opacity: 0.85;
    color: var(--color-brand-600);
  }

  &:hover {
    background: linear-gradient(
      90deg,
      rgba(var(--color-brand-300-rgb), 0.28),
      rgba(var(--color-brand-300-rgb), 0.1)
    );
    color: var(--color-text-strong);
    transform: translateX(2px);

    svg {
      opacity: 1;
    }
  }
`;

export const DropdownItemLabel = styled.span`
  flex: 1;
`;

export const DropdownItemBadge = styled.span`
  font-size: 10px;
  font-weight: 800;
  font-family: var(--font-family-primary);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 3px 8px;
  border-radius: var(--radius-pill);
  background: ${(props) =>
    props.$glow
      ? "linear-gradient(135deg, rgba(255, 200, 55, 0.32), rgba(255, 160, 20, 0.22))"
      : "linear-gradient(135deg, rgba(67, 233, 123, 0.22), rgba(56, 249, 215, 0.14))"};
  color: ${(props) => (props.$glow ? "#c57a00" : "#14804a")};
  border: 0.5px solid
    ${(props) =>
      props.$glow ? "rgba(255, 176, 46, 0.38)" : "rgba(67, 233, 123, 0.34)"};
  box-shadow: ${(props) =>
    props.$glow
      ? "inset 0 1px 0.4px rgba(255, 255, 255, 0.6)"
      : "inset 0 1px 0.4px rgba(67, 233, 123, 0.38)"};
  [data-theme="dark"] & {
    box-shadow: none;
    color: ${(props) => (props.$glow ? "#fff" : "#fff")};
  }
`;

export const DropdownSeparator = styled.div`
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(var(--color-brand-300-rgb), 0.28),
    transparent
  );
  margin: 0 16px;
`;

export const DropdownFooter = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px;
  border-top: 1px solid rgba(var(--color-brand-300-rgb), 0.14);
`;

export const Row = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const EmailText = styled.div`
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 400;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.01em;
  width: 100%;
`;

export const ThemeIcon = styled(FaMoon)`
  font-size: 1.4rem;
`;

export const PlayIcon = styled(FaCirclePlay)`
  height: 18px;
  width: 18px;
`;

export const PauseIcon = styled(FaCirclePause)`
  height: 18px;
  width: 18px;
`;

export const LogoutIcon = styled(FaSignOutAlt)`
  font-size: 1.4rem;
`;

export const BalanceContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 10px;
`;

export const BalanceValue = styled.span`
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.03em;
`;

export const BuyNow = styled.span`
  color: var(--color-brand-600);
  font-family: var(--font-family-primary);
  text-align: center;
  font-size: 14px;
  align-self: center;
  font-weight: 700;
  letter-spacing: 0.01em;
  text-decoration-line: underline;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    transform: translateY(-2px);
    color: var(--color-brand-700);
  }
`;

export const Text = styled.span`
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 14px;
  align-self: center;
  text-align: center;
  font-weight: 400;
  margin-top: -11px;
  letter-spacing: 0.01em;
`;
